import {useMemo, useState} from 'react';
import styles from './AppealStoriesTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, DropdownMenu, Icon} from '@gravity-ui/uikit';
import {Pencil, TrashBin} from '@gravity-ui/icons';
import {DataTable, formatDateToLocalDate} from '../../shared';
import {FundraiserStoryModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {DeleteStoryByIdModal, EditAppealStoryModal} from '../../features';
import {useNavigate} from 'react-router';

interface AppealStoriesTableProps {
  stories: FundraiserStoryModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  appealId: string;
}

export const AppealStoriesTable = ({
  stories,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  appealId,
}: AppealStoriesTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedStoryId, setSelectedStoryId] = useState<string | null>(null);

  const handleRowClick = (story: FundraiserStoryModel) => {
    navigate(`/fundraisings/appeals/${appealId}/stories/${story.id}`);
  };

  const handleDeleteStory = (story: FundraiserStoryModel) => {
    setSelectedStoryId(story.id);
    setIsDeleteModalOpen(true);
  };

  const handleEditStory = (story: FundraiserStoryModel) => {
    setSelectedStoryId(story.id);
    setIsEditModalOpen(true);
  };

  const columns = useMemo<TableColumnConfig<FundraiserStoryModel>[]>(
    () => [
      {
        id: 'title',
        name: 'Заголовок истории',
        template: (item: FundraiserStoryModel) => <Text variant="body-1">{item.title}</Text>,
      },
      {
        id: 'date',
        name: 'Дата создания',
        template: (item: FundraiserStoryModel) => <Text variant="body-1">{formatDateToLocalDate(item.date)}</Text>,
      },
      {
        id: 'priority',
        name: 'Приоритет',
        template: (item: FundraiserStoryModel) => <Text variant="body-1">{item.priority}</Text>,
      },
      {
        id: 'more',
        name: () => <div className={styles.tableHeader}></div>,
        template: (item: FundraiserStoryModel) => (
          <div className={styles.moreCell} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={Pencil} />,
                  action: () => handleEditStory(item),
                  text: 'Редактировать',
                },
                {
                  iconStart: <Icon size={16} data={TrashBin} />,
                  action: () => handleDeleteStory(item),
                  text: 'Удалить',
                  className: styles.dropdownItemDelete,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<FundraiserStoryModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={stories || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Истории не найдены"
        emptyStateDescription="У данного сбора пока нет историй. Добавьте первую историю"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}

      {selectedStoryId && (
        <DeleteStoryByIdModal
          open={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          storyId={selectedStoryId}
          appealId={appealId}
        />
      )}

      {selectedStoryId && (
        <EditAppealStoryModal
          open={isEditModalOpen}
          setOpen={setIsEditModalOpen}
          storyId={selectedStoryId}
          appealId={appealId}
        />
      )}
    </div>
  );
};
